import type { ThemeConfig } from "antd";
import { theme as antdTheme } from "antd";

/**
 * LonMax AI Portal theme.
 *
 * Colours mirror the shared tokens in packages/ui/src/styles.css, which are
 * derived from the four base colours in docs/PRODUCT.md §10.2 and validated
 * by scripts/contrast-check.mjs. Every colour, radius, and control height
 * lives here so component styling stays inside Ant Design's token system
 * rather than accumulating descendant CSS overrides. `styles/shell.css`
 * aliases the same values as CSS custom properties for the few surfaces
 * tokens cannot express.
 */
export const palette = {
  // Brand: 500 = identity, 600 = action fill / links, 700 = hover.
  brandIdentity: "#186dff",
  brand: "#1068ef",
  brandHover: "#0758db",
  // Text hierarchy per PRODUCT.md §10.3.
  ink: "#262626",
  copy: "#4d4d4d",
  muted: "#6b6b6b",
  disabled: "#bfbfbf",
  line: "#e7ebf1",
  lineSoft: "#f0f3f7",
  lineStrong: "#e2e6ec",
  contentBg: "#fcfdfe",
  // Semantic bases from PRODUCT.md with text-safe usage via antd derivation.
  success: "#22c472",
  successText: "#0e7a45",
  danger: "#fb3849",
  dangerText: "#ce1831",
  warning: "#fc932e",
  warningText: "#9a5700",
  groupLabel: "#6b6b6b",
  navItem: "#4d4d4d",
  rowHover: "#f0f3f7",
} as const;

export const darkPalette = {
  // Graphite dark family — matches [data-theme="dark"] in packages/ui.
  brandIdentity: "#79a6ff",
  brand: "#1068ef",
  brandHover: "#0758db",
  brandText: "#79a6ff",
  brandTextHover: "#9dc3ff",
  ink: "#e8ebef",
  copy: "#adb4be",
  muted: "#8a929c",
  disabled: "#5c646d",
  line: "#2a3038",
  lineSoft: "#232830",
  lineStrong: "#363d46",
  contentBg: "#101317",
  containerBg: "#171b20",
  subtleBg: "#1e2329",
  success: "#3fcf8b",
  successText: "#3fcf8b",
  danger: "#ff6b77",
  dangerText: "#ff6b77",
  warning: "#ffab57",
  warningText: "#ffab57",
  groupLabel: "#8a929c",
  navItem: "#adb4be",
  rowHover: "#1e2329",
} as const;

/** Structural constants shared between the shell component and its stylesheet. */
export const metrics = {
  railWidth: 236,
  headerHeight: 78,
  panelWidth: 348,
  /** Below this width the details panel becomes a Drawer (preview @1280px). */
  panelBreakpoint: 1280,
  /** Below this width the rail becomes a Drawer. */
  railBreakpoint: 768,
} as const;

function buildTheme(dark: boolean): ThemeConfig {
  const p = dark ? darkPalette : palette;
  return {
    algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: p.brand,
      colorInfo: p.brand,
      colorSuccess: p.success,
      colorError: p.danger,
      colorWarning: p.warning,
      colorLink: dark ? darkPalette.brandText : palette.brand,
      colorLinkHover: dark ? darkPalette.brandTextHover : palette.brandHover,
      colorText: p.ink,
      colorTextSecondary: p.copy,
      colorTextTertiary: p.muted,
      colorTextDescription: p.copy,
      colorTextDisabled: p.disabled,
      colorBorder: dark ? darkPalette.lineStrong : palette.lineStrong,
      colorBorderSecondary: p.lineSoft,
      colorBgContainer: dark ? darkPalette.containerBg : "#ffffff",
      colorBgLayout: p.contentBg,
      colorSplit: p.lineSoft,
      borderRadius: 10,
      borderRadiusLG: 14,
      borderRadiusSM: 8,
      controlHeight: 40,
      fontSize: 14,
      // Keep in sync with --as-font in packages/ui: Inter for Latin,
      // Noto Sans SC (self-hosted by the Portal) for CJK.
      fontFamily: '"Inter Variable", "Inter", "Noto Sans SC Variable", "Noto Sans SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", "Segoe UI", system-ui, -apple-system, sans-serif',
      fontFamilyCode: '"JetBrains Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
      lineHeight: 22 / 14,
      wireframe: false,
    },
    components: {
      Layout: {
        siderBg: "transparent",
        headerBg: "transparent",
        headerHeight: metrics.headerHeight,
        headerPadding: "0 26px",
        bodyBg: p.contentBg,
        footerBg: p.contentBg,
      },
      Menu: {
        itemBg: "transparent",
        subMenuItemBg: "transparent",
        itemColor: p.navItem,
        itemHoverBg: dark ? "rgba(255,255,255,.06)" : "#f0f3f7",
        itemHoverColor: p.ink,
        itemSelectedBg: dark ? "rgba(121,166,255,.14)" : "#eef5ff",
        itemSelectedColor: dark ? darkPalette.brandText : palette.brand,
        itemHeight: 40,
        itemBorderRadius: 10,
        itemMarginBlock: 2,
        itemMarginInline: 4,
        itemPaddingInline: 14,
        iconSize: 18,
        iconMarginInlineEnd: 12,
        collapsedIconSize: 18,
        groupTitleColor: p.groupLabel,
        groupTitleFontSize: 12,
        groupTitleLineHeight: 1.6,
        activeBarWidth: 0,
        activeBarBorderWidth: 0,
      },
      Card: {
        headerBg: "transparent",
        headerFontSize: 19,
        headerHeight: 56,
        bodyPadding: 0,
        paddingLG: 20,
        colorBorderSecondary: p.line,
      },
      Table: {
        headerBg: "transparent",
        headerColor: p.muted,
        headerSplitColor: "transparent",
        headerBorderRadius: 0,
        borderColor: p.lineSoft,
        rowHoverBg: p.rowHover,
        cellPaddingBlock: 15,
        cellPaddingInline: 16,
        footerBg: "transparent",
      },
      Segmented: {
        trackBg: dark ? darkPalette.subtleBg : "#f0f3f7",
        trackPadding: 4,
        itemColor: p.copy,
        itemHoverColor: p.ink,
        itemSelectedBg: dark ? darkPalette.containerBg : "#ffffff",
        itemSelectedColor: dark ? darkPalette.brandText : palette.brand,
        borderRadius: 8,
        borderRadiusSM: 8,
        controlHeight: 40,
      },
      Tabs: {
        itemColor: p.muted,
        itemHoverColor: dark ? "#c9d1dc" : "#3d3d3d",
        itemSelectedColor: dark ? darkPalette.brandText : palette.brand,
        itemActiveColor: dark ? darkPalette.brandText : palette.brand,
        inkBarColor: dark ? darkPalette.brandText : palette.brand,
        titleFontSize: 14,
        horizontalItemGutter: 24,
        horizontalItemPadding: "0 0 11px",
        cardBg: "transparent",
      },
      Descriptions: {
        labelColor: p.muted,
        contentColor: p.ink,
        itemPaddingBottom: 8,
        titleMarginBottom: 6,
      },
      Tag: {
        borderRadiusSM: 20,
        defaultBg: p.lineSoft,
        defaultColor: p.copy,
      },
      Breadcrumb: {
        itemColor: p.copy,
        lastItemColor: dark ? "#dbe1e9" : "#3d3d3d",
        separatorColor: dark ? "#4a525c" : "#c9cfd8",
        linkColor: p.copy,
        linkHoverColor: dark ? darkPalette.brandText : palette.brand,
        separatorMargin: 9,
      },
      Pagination: {
        itemSize: 32,
        itemActiveBg: dark ? "#20262e" : "#ffffff",
        colorPrimary: p.brand,
        colorPrimaryHover: p.brandHover,
      },
      Button: {
        defaultColor: p.copy,
        defaultBorderColor: dark ? darkPalette.lineStrong : "#e2e6ec",
        defaultHoverBg: dark ? darkPalette.subtleBg : "#f4f6f9",
        defaultHoverColor: p.ink,
        defaultHoverBorderColor: dark ? darkPalette.lineStrong : "#e2e6ec",
        textHoverBg: dark ? "rgba(255,255,255,.06)" : "#f0f3f7",
        paddingInline: 14,
        fontWeight: 500,
      },
      Input: {
        hoverBorderColor: p.muted,
        activeBorderColor: dark ? darkPalette.brandText : palette.brand,
        activeShadow: dark ? "0 0 0 3px rgba(121,166,255,.16)" : "0 0 0 3px rgba(24,109,255,.12)",
        colorTextPlaceholder: p.muted,
      },
      Select: {
        optionSelectedBg: dark ? "rgba(121,166,255,.16)" : "#eef5ff",
        optionSelectedColor: dark ? darkPalette.brandText : palette.brand,
        colorTextPlaceholder: p.muted,
      },
      Drawer: {
        footerPaddingBlock: 14,
        footerPaddingInline: 20,
      },
      Dropdown: {
        borderRadiusLG: 14,
        paddingBlock: 6,
      },
      Avatar: {
        colorTextPlaceholder: dark ? darkPalette.brandText : palette.brand,
      },
      Tooltip: {
        colorBgSpotlight: dark ? "#222932" : "#2f3338",
        borderRadius: 6,
        fontSize: 12,
      },
      Empty: {
        colorTextDescription: p.muted,
      },
      Result: {
        titleFontSize: 20,
      },
    },
  };
}

/** Themes are built once per mode — rebuilding per render forces antd to
 *  re-derive every component style. */
const themes = { light: buildTheme(false), dark: buildTheme(true) };

export const portalTheme = themes.light;
export const portalDarkTheme = themes.dark;
export const themeFor = (dark: boolean): ThemeConfig => (dark ? themes.dark : themes.light);
export { buildTheme };
